import _ from "lodash";
import React, { useRef } from "react";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import { prepareLoadMoreFeed } from "Utils";
import { useHandleScroll } from "Utils/CustomHooks";
import withLoader from "UIComponents/GqlWithLoader";
import PaginationLoader from "UIComponents/PaginationLoader";
import I18nHOC from "UIComponents/I18nHOC";
import NoDataComponent from "UIComponents/NoDataComponent";
import { getOrganizationAssignmentFeedQuery } from "ClassRoom/modules/ClassRoomQuery";
import { getOrganizationAssignmentFeedFromCache } from "ClassRoom/modules/ClassRoomGraphqlHelpers";
import { getCurrentAssessmentTypes } from "Platform/modules/PlatformModule";
import AssignmentFeed from "./AssignmentFeed";
import { getAssignmentsFromEdgesMemoized } from "./AssignmentSelectionUtils";
import classes from "./AssignmentSelection.scss";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { KeyboardNavigableList } from "UIComponents/KeyboardNavigableList";

const AssignmentSelection = props => {
  const {
    assignments,
    selectedItems,
    updateSelectedItems,
    disabledItems,
    customOverlayElement,
    customEmptyViewElement,
    customHeaderElement,
    networkStatus,
    loadMoreFeed,
    hasNextPage,
    isFetchingMoreData,
    customCardComponent,
    styles,
    t,
    totalCount,
    testId,
  } = props;

  const scrollContainerRef = useRef({});
  const assignmentFeedContainerRef = useRef(null);

  const getScrollRef = () => {
    return scrollContainerRef.current;
  };

  useHandleScroll({ networkStatus, loadMoreFeed, hasNextPage, getScrollRef });

  const assignmentsCount = _.size(assignments);
  const areAssignmentsEmpty = assignmentsCount == 0;

  return (
    <div
      className={classes.container}
      ref={scrollContainerRef}
      style={_.get(styles, "container", {})}
    >
      <div className={classes.innerContainer}>
        {customHeaderElement &&
          React.cloneElement(customHeaderElement, {
            count: assignmentsCount,
            totalCount,
          })}
        {!areAssignmentsEmpty ? (
          <>
            <KeyboardNavigableList
              listContainerRef={assignmentFeedContainerRef}
            >
              <div
                ref={assignmentFeedContainerRef}
                className={classes.feedContainer}
                style={_.get(styles, "feedContainer", {})}
              >
                <AssignmentFeed
                  assignments={assignments}
                  selectedItems={selectedItems}
                  updateSelectedItems={updateSelectedItems}
                  disabledItems={disabledItems}
                  customOverlayElement={customOverlayElement}
                  styles={styles}
                  customCardComponent={customCardComponent}
                  testId={testId}
                />
              </div>
            </KeyboardNavigableList>
            {isFetchingMoreData && <PaginationLoader />}
          </>
        ) : customEmptyViewElement ? (
          customEmptyViewElement
        ) : (
          <div className={classes.emptyStateContainer}>
            <NoDataComponent
              isSearch={false}
              emptyHeaderText={t("common:no_data_found")}
              emptyText={""}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = state => {
  const userInfo = getUserInfo();
  const organizationId = userInfo.org_id;

  return {
    organizationId,
  };
};

const mapActionCreators = { getCurrentAssessmentTypes };

const composedAssignmentSelection = compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getOrganizationAssignmentFeedQuery, {
    name: "getOrganizationAssignmentFeed",
    alias: "getOrganizationAssignmentFeed",
    options: ({
      organizationId,
      first,
      filters,
      orderBy,
      orderByDirection,
    }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId,
          first,
          orderBy,
          orderByDirection,
          filters,
        },
      };
    },
    props({
      getOrganizationAssignmentFeed,
      ownProps: {
        organizationId,
        first,
        filters,
        orderBy,
        orderByDirection,
        getCurrentAssessmentTypes,
        t,
      },
    }) {
      const queryData = getOrganizationAssignmentFeedFromCache({
        organizationId,
        first,
        orderBy,
        orderByDirection,
        filters,
      });

      const hasNextPage = _.get(
        queryData,
        "assignments.pageInfo.hasNextPage",
        false
      );
      const networkStatus = _.get(
        getOrganizationAssignmentFeed,
        "networkStatus"
      );

      const endCursor = _.get(
        getOrganizationAssignmentFeed,
        "node.assignments.pageInfo.endCursor",
        ""
      );

      const totalCount = _.get(queryData, "assignments.totalCount", 0);

      const edges = _.get(queryData, "assignments.edges", []);
      const currentAssessmentTypes = getCurrentAssessmentTypes();
      const assignments = getAssignmentsFromEdgesMemoized({
        data: edges,
        t,
        currentAssessmentTypes,
      });

      return {
        assignments,

        hasNextPage,
        isData: !_.isEmpty(queryData),
        networkStatus,
        isLoading: _.includes([1, 2], networkStatus),
        isFetchingMoreData: networkStatus == 3,
        totalCount,
        loadMoreFeed: prepareLoadMoreFeed({
          variables: {
            organizationId,
            first,
            orderBy,
            orderByDirection,
            filters,
            after: endCursor,
          },
          queryData: getOrganizationAssignmentFeed,
          key: "assignments",
        }),
      };
    },
  }),
  withLoader
)(AssignmentSelection);

export default composedAssignmentSelection;

composedAssignmentSelection.defaultProps = {
  first: 20,
  orderBy: "PUBLISHED_AT",
  orderByDirection: "DESC",
  filters: {
    contentTypes: ["ASSESSMENT", "ASSIGNMENT_RESOURCE"],
    states: ["PUBLISHED"],
  },
  customOverlayElement: null,
};
